/**
 * Pure helpers for the Edit-Draft "Add Lines from Unbilled Work" picker.
 *
 * The picker reads one server page of unbilled WIP at a time, so every rule that
 * used to run over a fully-loaded list now runs over the loaded page plus local
 * draft state. These helpers are kept out of InvoiceGenerationWizard so they can
 * be unit-tested without rendering the wizard.
 */

import { matchesWipSearchTokens, splitWipSearchTokens } from './wip-filtering';

/** Row shape rendered by the picker (a subset of the wizard's WipItem). */
export interface PickerWipRow {
  id: string;
  date: string;
  resource: string;
  description: string;
  type: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
  costCentre?: string;
  reference?: string;
  costRate?: number;
  role?: string;
}

/** Subset of the wizard's InvoiceLineItem needed by these helpers. */
export interface PickerDraftLine {
  originalWipId?: string;
  date: Date;
  type: string;
  resource: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
  costCentre?: string;
  reference?: string;
  costRate?: number;
  role?: string;
}

/** WIP ids of the lines currently on the draft. */
export function draftWipIds(lines: Array<{ originalWipId?: string }>): Set<string> {
  return new Set(
    lines
      .map((li) => li.originalWipId)
      .filter((id): id is string => Boolean(id))
  );
}

/**
 * Drops page rows that are already on the draft. Only lines added during this
 * session can match: saved draft lines are Status=1 server-side and so are absent
 * from the unbilled feed. A page can therefore legitimately render fewer rows
 * than the requested page size.
 */
export function excludeExistingDraftRows<T extends { id: string }>(
  rows: T[],
  existingWipIds: Set<string>
): T[] {
  return rows.filter((row) => !existingWipIds.has(row.id));
}

/**
 * Saved draft lines the user removed in this session, shaped as picker rows so
 * they can be offered back. They are not in the unbilled feed (still Draft
 * server-side), so they come from the seeded snapshot instead.
 */
export function toReinstatableRows(
  removedOriginalLines: PickerDraftLine[],
  rowsOnPage: Array<{ id: string }> = []
): PickerWipRow[] {
  const onPage = new Set(rowsOnPage.map((row) => row.id));
  return removedOriginalLines
    .filter((li) => li.originalWipId && !onPage.has(li.originalWipId))
    .map((li) => ({
      id: li.originalWipId as string,
      date: li.date.toISOString(),
      resource: li.resource,
      description: li.description,
      type: li.type,
      quantity: li.quantity,
      unit: li.unit,
      rate: li.rate,
      amount: li.amount,
      costCentre: li.costCentre,
      reference: li.reference,
      costRate: li.costRate,
      role: li.role,
    }));
}

/**
 * Lines pulled from the unbilled feed during this session and not yet saved.
 * They are still Status=0, so the server's unbilled total still counts them.
 */
export function countSessionAddedFromFeed(
  invoiceLineItems: Array<{ originalWipId?: string }>,
  originalDraftLines: Array<{ originalWipId?: string }>
): number {
  const savedWipIds = draftWipIds(originalDraftLines);
  return invoiceLineItems.filter(
    (li) => li.originalWipId && !savedWipIds.has(li.originalWipId)
  ).length;
}

/**
 * Count shown on the "Add Lines" badge: the project-wide unbilled total, less
 * the feed rows already sitting on the draft, plus the locally reinstatable
 * lines. Never negative (the server total can go stale while the user edits).
 */
export function computeAddLinesBadgeCount(args: {
  serverTotalCount: number;
  sessionAddedFromFeedCount: number;
  reinstatableCount: number;
}): number {
  const unbilled = Math.max(0, args.serverTotalCount - args.sessionAddedFromFeedCount);
  return unbilled + args.reinstatableCount;
}

/** Toggles a row in the selection map, keeping the row data for later materialisation. */
export function toggleRowSelection<T extends { id: string }>(
  selected: Map<string, T>,
  row: T
): Map<string, T> {
  const next = new Map(selected);
  if (next.has(row.id)) next.delete(row.id);
  else next.set(row.id, row);
  return next;
}

/** Selects or deselects every row on the current page, leaving other pages' selections intact. */
export function setPageSelection<T extends { id: string }>(
  selected: Map<string, T>,
  rows: T[],
  isSelected: boolean
): Map<string, T> {
  const next = new Map(selected);
  for (const row of rows) {
    if (isSelected) next.set(row.id, row);
    else next.delete(row.id);
  }
  return next;
}

/**
 * Case-insensitive match over the fields the picker's search box covers, using
 * the same whitespace-token rule as the stored procedure so locally matched
 * rows (reinstatable lines) agree with the server's page.
 */
export function matchesPickerSearch(row: PickerWipRow, query: string): boolean {
  return matchesWipSearchTokens(
    [row.description, row.resource, row.type, row.costCentre, row.reference],
    splitWipSearchTokens(query)
  );
}
